// The live half of the Loremaster's Compass character lane, the pure part.
// What the DM Screen sends Compass for each D&D Beyond character (its live
// state: HP, temp HP, max HP, AC, coins, conditions, inventory rows, prepared
// spells), and how a Compass edit lands back on a Character. The transport
// (window.postMessage to the connector's lmc-live-screen.js) lives in
// components/CompassLive.tsx; this file has no side effects so it can be
// reasoned about and, later, tested.
import type { Character } from "./types";

export interface LiveState {
  characterId: string;
  name: string;
  /* Optional on purpose. A character whose hit points this screen does not
     actually know must not be published as 0: Compass applies the lane without
     a review screen, and zero hit points is a real and serious state in play, so
     an invented 0 is indistinguishable from the truth. Absent means unknown;
     0 means none. */
  hp?: number; tempHp?: number; maxHp?: number; ac?: number;
  coins: { pp: number; gp: number; ep: number; sp: number; cp: number };
  conditions: string[];
  inventory: { name: string; qty: number; equipped: boolean; attuned: boolean }[];
  preparedSpells: string[];
  at: number;
}

/** A Compass edit as the connector hands it over: only what Compass speaks for. */
export interface LiveApply {
  id: number;
  characterId: string;
  fields: Partial<Pick<LiveState, "hp" | "tempHp" | "maxHp" | "ac" | "coins" | "conditions">> & { at?: number };
}

const num = (v: unknown, fallback = 0) => { const n = Number(v); return Number.isFinite(n) ? n : fallback; };
/** A number, or nothing -- never a zero standing in for "unknown".
 *
 *  `Number("")` is 0, so every blank that reached `num` became a real, actable
 *  zero. This is the same rule `valueForRole` already applies to a token bar
 *  (an empty bar reads as "unknown", a number reads as a fact); it was simply
 *  never applied to the lane that writes to Compass's durable records. */
const finite = (v: unknown): number | null => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
/** Copy a number onto a patch only when it IS one. */
const put = <K extends string>(into: Record<string, unknown>, key: K, v: unknown) => { const n = finite(v); if (n !== null) into[key] = n; };
export const sheetIdOf = (c: Pick<Character, "id">) => (/^ddb-(\d+)$/.exec(String(c.id || "")) || [])[1] || "";
export const conditionName = (c: unknown) => typeof c === "string" ? c : String((c as { name?: string; condition?: string } | null)?.name || (c as { condition?: string } | null)?.condition || "");

/** The live state of one character, or null for one that is not a D&D Beyond sheet. */
export function liveStateOf(c: Character, at = Date.now()): LiveState | null {
  const characterId = sheetIdOf(c);
  if (!characterId) return null;
  const rows = Array.isArray(c.inventoryItems) ? (c.inventoryItems as Array<Record<string, unknown>>) : [];
  const equipped = new Set((Array.isArray(c.equippedItems) ? c.equippedItems : []).map((x) => String((x as { name?: string })?.name || x || "").toLowerCase()));
  const vitals: Record<string, unknown> = {};
  put(vitals, "hp", c.hp); put(vitals, "tempHp", c.tempHp); put(vitals, "maxHp", c.maxHp); put(vitals, "ac", c.ac);
  return {
    characterId, name: String(c.name || ""), at,
    ...vitals,
    coins: { pp: num(c.coins?.pp), gp: num(c.coins?.gp), ep: num(c.coins?.ep), sp: num(c.coins?.sp), cp: num(c.coins?.cp) },
    conditions: (c.conditions || []).map(conditionName).filter(Boolean),
    inventory: rows.filter((r) => r && typeof r.name === "string" && String(r.name).trim()).map((r) => ({
      name: String(r.name).trim(), qty: num(r.quantity, 1),
      equipped: r.equipped === true || equipped.has(String(r.name).toLowerCase()),
      attuned: r.attuned === true,
    })),
    preparedSpells: (Array.isArray(c.preparedSpells) ? c.preparedSpells : []).map((s) => typeof s === "string" ? s : String((s as { name?: string })?.name || "")).filter(Boolean),
  };
}

/** Every sheet character's live state, in roster order. */
export function liveSnapshot(characters: Character[], at = Date.now()): LiveState[] {
  return characters.map((c) => liveStateOf(c, at)).filter((s): s is LiveState => !!s);
}

/** A stable fingerprint of what would be sent, minus the clock, so an unchanged roster is not re-sent. */
export function liveFingerprint(states: LiveState[]): string {
  return JSON.stringify(states.map((s) => ({ ...s, at: 0 })));
}

/** Apply a Compass edit to a Character. Returns the same object when nothing moved. */
export function applyLive(c: Character, fields: LiveApply["fields"]): Character {
  let next = c; const set = (patch: Partial<Character>) => { next = next === c ? { ...c, ...patch } : { ...next, ...patch }; };
  /* A value this edit cannot read is LEFT ALONE rather than written as 0. A
     real 0 still lands: unconscious is a state the table must see. */
  const take = (key: "hp" | "tempHp" | "maxHp" | "ac") => {
    if (fields[key] === undefined) return;
    const n = finite(fields[key]);
    if (n !== null && n !== num(c[key])) set({ [key]: n } as Partial<Character>);
  };
  take("hp"); take("tempHp"); take("maxHp"); take("ac");
  if (fields.coins && typeof fields.coins === "object") {
    const coins = { pp: num(fields.coins.pp), gp: num(fields.coins.gp), ep: num(fields.coins.ep), sp: num(fields.coins.sp), cp: num(fields.coins.cp) };
    const cur = c.coins || { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 };
    if ((["pp", "gp", "ep", "sp", "cp"] as const).some((k) => coins[k] !== num(cur[k]))) set({ coins });
  }
  if (Array.isArray(fields.conditions)) {
    const incoming = fields.conditions.map((x) => String(x).trim()).filter(Boolean);
    const current = (c.conditions || []).map(conditionName);
    if (incoming.join("") !== current.join("")) {
      // keep the DM Screen's own condition objects where the name still stands
      const byName = new Map((c.conditions || []).map((x) => [conditionName(x).toLowerCase(), x] as const));
      set({ conditions: incoming.map((n) => byName.get(n.toLowerCase()) ?? n) });
    }
  }
  return next;
}

/** One line for the activity log: what a Compass edit changed. */
export function describeLive(before: Character, after: Character): string {
  const parts: string[] = [];
  if (before.hp !== after.hp) parts.push(`HP ${before.hp}→${after.hp}`);
  if (before.tempHp !== after.tempHp) parts.push(`temp ${before.tempHp}→${after.tempHp}`);
  if (before.maxHp !== after.maxHp) parts.push(`max HP ${before.maxHp}→${after.maxHp}`);
  if (before.ac !== after.ac) parts.push(`AC ${before.ac}→${after.ac}`);
  (["pp", "gp", "ep", "sp", "cp"] as const).forEach((k) => { if (num(before.coins?.[k]) !== num(after.coins?.[k])) parts.push(`${k} ${num(before.coins?.[k])}→${num(after.coins?.[k])}`); });
  const b = new Set((before.conditions || []).map(conditionName)), a = new Set((after.conditions || []).map(conditionName));
  [...a].filter((x) => !b.has(x)).forEach((x) => parts.push(`+${x}`));
  [...b].filter((x) => !a.has(x)).forEach((x) => parts.push(`−${x}`));
  return parts.join(" · ");
}
